// The lifecycle notification vocabulary (CHT-764, Phase 12.4; docs/architecture/milestone-e-jobs-contract.md section 7). A PRODUCER writes one
// outbox row (`notification_events`) in the transaction of its own change. A later consumer (the `notification.deliver` task) reads the
// row, applies the delivery policy and sends a push. Delivery never runs inside the producer's transaction, so a failed push cannot undo
// a payment, a request or a trip.
//
// A row holds IDS and CLOSED CODES only. The kinds are UPPER CASE codes because the outbox column checks that form
// (`notification_events_kind_format`). The contract text names them in dotted lower case (`request.sla_escalated`); the stored code is
// `REQUEST_SLA_ESCALATED`.
/**
 * The kinds that the Phase 12 producers write, in addition to the request kinds that lane R2 already writes
 * (`requestNotificationKinds`). A new kind is added here, together with its row in the delivery table of `notification-policy.ts`.
 */
export const lifecycleNotificationKinds = Object.freeze([
    // Phase 12.3: the request SLA sweep tells the staff queue that a request is late, and the customer that it waits for an answer.
    "REQUEST_SLA_ESCALATED",
    "REQUEST_CUSTOMER_REMINDER",
    // Phase 11 (payments) and the payment expiry job of Phase 12.3. The first payment kind is `REQUEST_PAYMENT_REQUIRED` (lane R2).
    "PAYMENT_EXPIRED",
    "PAYMENT_PAID",
    // Phase 12.5: proactive trip reminder, weather change, and a staff signal for a trip that needs attention.
    "TRIP_UPCOMING",
    "ITINERARY_WEATHER_RISK",
    "TRIP_ATTENTION_NEEDED",
]);
/** The subjects a lifecycle row can name. The delivery task re-reads the subject before it sends. */
export const lifecycleSubjectTypes = Object.freeze([
    "concierge_request",
    "payment_request",
    "trip",
    "itinerary_item",
]);
/** The most characters of a dedupe key (the outbox column check). */
export const NOTIFICATION_DEDUPE_KEY_MAX = 200;
const CLOSED_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;
const KEY_PART = /^[A-Za-z0-9._-]{1,64}$/;
export const isClosedNotificationCode = (value) => CLOSED_CODE.test(value);
/**
 * The unique key of a lifecycle row: `<kind>:<subjectId>:<version or stage>:<audience>:<recipient id or "queue">`. One part never holds
 * a `:`, so one part cannot forge another. The same event writes the same key, so a replay writes nothing twice.
 */
export function lifecycleDedupeKey(input) {
    const stage = typeof input.versionOrStage === "number" ? `v${input.versionOrStage}` : input.versionOrStage;
    if (typeof input.versionOrStage === "number" && !Number.isSafeInteger(input.versionOrStage)) {
        throw new RangeError("the version of a notification key is a whole number");
    }
    const key = [
        input.kind,
        input.subjectId,
        stage,
        input.audience,
        input.recipientId ?? "queue",
    ].map((part) => {
        if (!KEY_PART.test(part))
            throw new RangeError("a part of a notification key is not valid");
        return part;
    });
    const text = key.join(":");
    if (text.length > NOTIFICATION_DEDUPE_KEY_MAX) {
        throw new RangeError("a notification key is at most 200 characters");
    }
    return text;
}
/** The outbox row of a lifecycle draft. The payload holds the subject version and the reason code only. */
export function outboxDraftOfLifecycle(draft) {
    if (draft.audience === "CUSTOMER" && draft.staffUserId !== null) {
        throw new RangeError("a customer notification names no staff user");
    }
    if (draft.reasonCode !== null && !CLOSED_CODE.test(draft.reasonCode)) {
        throw new RangeError("the reason of a notification is a closed upper case code");
    }
    if (draft.subjectDate != null && !/^\d{4}-\d{2}-\d{2}$/.test(draft.subjectDate)) {
        throw new RangeError("the date of a notification is YYYY-MM-DD");
    }
    if (draft.dedupeKey.length === 0 || draft.dedupeKey.length > NOTIFICATION_DEDUPE_KEY_MAX) {
        throw new RangeError("a notification key is 1 to 200 characters");
    }
    return Object.freeze({
        kind: draft.kind,
        audience: draft.audience,
        customerId: draft.customerId,
        staffUserId: draft.staffUserId,
        subjectType: draft.subject.type,
        subjectId: draft.subject.id,
        dedupeKey: draft.dedupeKey,
        payload: Object.freeze({
            v: 1,
            subjectVersion: draft.subjectVersion,
            reasonCode: draft.reasonCode,
            subjectDate: draft.subjectDate ?? null,
        }),
        ...(draft.occurredAt === undefined ? {} : { createdAt: draft.occurredAt }),
    });
}
